// VerifyEmail.js - Email verification gate for new accounts
//
// TWO modes — determined by URL query parameters:
//
//   ACTION MODE  (user clicked the Firebase verification link in their email)
//   URL:  /verify-email?mode=verifyEmail&oobCode=XXXX&apiKey=XXXX
//   Flow: calls applyActionCode(auth, oobCode) → success/error page
//
//   WAITING MODE (user just signed up and hasn't clicked the link yet)
//   URL:  /verify-email  (no params)
//   Flow: polls every 4s, resend link, manual fallback button

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auth, db } from '../firebase';
import { sendEmailVerification, applyActionCode } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Returns the correct next step after email verification, based on Firestore role.
// Customers → /dashboard  (Dashboard.js handles onboarding + terms gating)
// Providers → /provider/onboarding  (first step after verification)
// Falls back to /dashboard if the user isn't logged in or the doc is missing.
async function getNextStepUrl() {
  const user = auth.currentUser;
  if (!user) return '/dashboard';
  try {
    const snap = await getDoc(doc(db, 'users', user.uid));
    if (snap.exists() && snap.data().role === 'provider') return '/provider/onboarding';
  } catch {
    // Fall through to customer default
  }
  return '/dashboard';
}


function VerifyEmail() {
  // ===== QUERY PARAMS =====
  // mode and oobCode are injected by Firebase when the user clicks the email link
  const [searchParams] = useSearchParams();
  const mode    = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // ===== ACTION MODE STATE =====
  // 'idle' briefly before the useEffect fires, then 'loading' | 'success' | 'error'
  const [codeStatus, setCodeStatus]   = useState('idle');
  const [codeError, setCodeError]     = useState('');
  const [dashboardUrl, setDashboardUrl] = useState('/dashboard');

  // ===== WAITING MODE STATE =====
  const [resendCooldown, setResendCooldown] = useState(0);
  const [checking, setChecking]             = useState(false);
  const [resendMsg, setResendMsg]           = useState('');
  const [justVerified, setJustVerified]     = useState(false);

  // ===== ACTION MODE — apply the verification code on mount =====
  // Only runs when the URL has mode=verifyEmail and a valid oobCode
  useEffect(() => {
    if (mode !== 'verifyEmail' || !oobCode) return;

    const runVerification = async () => {
      setCodeStatus('loading');
      try {
        // Apply the one-time code — marks the email as verified in Firebase Auth
        await applyActionCode(auth, oobCode);

        // Reload the current session so emailVerified updates immediately
        if (auth.currentUser) {
          try { await auth.currentUser.reload(); } catch { /* ignore if offline */ }
        }

        // Determine next step URL after email is verified
        const url = await getNextStepUrl();
        setDashboardUrl(url);
        setCodeStatus('success');

      } catch (err) {
        console.error('Email verification error:', err);

        // Map Firebase error codes to readable messages
        if (
          err.code === 'auth/invalid-action-code' ||
          err.code === 'auth/invalid-oob-code'
        ) {
          setCodeError('This verification link is invalid or has already been used. Please request a new one.');
        } else if (err.code === 'auth/expired-action-code') {
          setCodeError('This verification link has expired. Please request a new one from the login page.');
        } else {
          setCodeError('Verification failed. The link may be invalid or expired. Please try again.');
        }
        setCodeStatus('error');
      }
    };

    runVerification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== WAITING MODE — auto-detect verification every 4 seconds =====
  // Only active when there are no action code params in the URL
  useEffect(() => {
    if (mode === 'verifyEmail') return; // handled by action mode above

    const interval = setInterval(async () => {
      if (!auth.currentUser) return;
      try {
        await auth.currentUser.reload();
        if (auth.currentUser.emailVerified) {
          clearInterval(interval);
          setJustVerified(true);
          const url = await getNextStepUrl();
          setTimeout(() => { window.location.href = url; }, 1500);
        }
      } catch {
        // Silently ignore reload errors — will retry next tick
      }
    }, 4000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== WAITING MODE — resend cooldown countdown =====
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // ===== HANDLERS =====

  const handleResend = async () => {
    if (!auth.currentUser || resendCooldown > 0) return;
    try {
      await sendEmailVerification(auth.currentUser);
      setResendCooldown(60);
      setResendMsg('Verification email sent.');
    } catch {
      setResendMsg('Could not resend. Try again shortly.');
    }
  };

  // Manual fallback — in case the 4-second poll misses it
  const handleCheck = async () => {
    setChecking(true);
    try {
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        setJustVerified(true);
        const url = await getNextStepUrl();
        setTimeout(() => { window.location.href = url; }, 1500);
      } else {
        setResendMsg("Email not verified yet. Check your inbox (and spam folder).");
      }
    } catch {
      setResendMsg('Something went wrong. Try again.');
    }
    setChecking(false);
  };

  // ===================================================================
  // ===== ACTION MODE RENDERS =====
  // ===================================================================

  if (mode === 'verifyEmail') {

    // Applying the code
    if (codeStatus === 'idle' || codeStatus === 'loading') {
      return (
        <div className="auth-page">
          <a href="/" className="auth-logo">Evermark Memorial</a>
          <div className="auth-card verify-email-card">
            <div className="verify-email-icon">📧</div>
            <h2 className="auth-title">Verifying your email…</h2>
            <p className="auth-subtitle">Just a moment.</p>
            <div className="loading-spinner" style={{ margin: '24px auto 0' }} />
          </div>
        </div>
      );
    }

    // Code applied successfully
    if (codeStatus === 'success') {
      return (
        <div className="auth-page">
          <a href="/" className="auth-logo">Evermark Memorial</a>
          <div className="auth-card verify-email-card">

            {/* Success icon */}
            <div className="verify-email-icon">✅</div>

            <h2 className="auth-title">Email verified!</h2>

            <p className="auth-subtitle" style={{ marginBottom: '8px' }}>
              Your email has been verified. Welcome to Evermark Memorial.
            </p>

            {/* Continue button — goes to the correct next step for this user's role */}
            <a href={dashboardUrl} style={{ display: 'block', width: '100%', marginTop: '28px' }}>
              <button className="btn-primary btn-full">
                Continue →
              </button>
            </a>

          </div>
        </div>
      );
    }

    // Code invalid or expired
    if (codeStatus === 'error') {
      return (
        <div className="auth-page">
          <a href="/" className="auth-logo">Evermark Memorial</a>
          <div className="auth-card verify-email-card">

            <div className="verify-email-icon">⚠️</div>

            <h2 className="auth-title">Verification failed</h2>

            <div className="auth-error" style={{ marginTop: '16px', marginBottom: '8px' }}>
              {codeError}
            </div>

            <p className="auth-helper-text" style={{ textAlign: 'center', marginTop: '16px' }}>
              <a href="/login" style={{ color: '#4a7c59' }}>Go to login</a>
              {' · '}
              <a href="/verify-email" style={{ color: '#4a7c59' }}>Request a new link</a>
            </p>

          </div>
        </div>
      );
    }
  }

  // ===================================================================
  // ===== WAITING MODE RENDERS (no query params) =====
  // ===================================================================

  // Brief success flash before auto-redirect
  if (justVerified) {
    return (
      <div className="auth-page">
        <a href="/" className="auth-logo">Evermark Memorial</a>
        <div className="auth-card verify-email-card">
          <div className="verify-email-icon">✅</div>
          <h2 className="auth-title">Email verified!</h2>
          <p className="auth-subtitle">Taking you to the next step…</p>
        </div>
      </div>
    );
  }

  // Default waiting state — user needs to click the link in their inbox
  return (
    <div className="auth-page">
      <a href="/" className="auth-logo">Evermark Memorial</a>
      <div className="auth-card verify-email-card">

        {/* Email icon */}
        <div className="verify-email-icon">📧</div>

        <h2 className="auth-title">Check your email</h2>
        <p className="auth-subtitle">
          We sent a verification link to{' '}
          <strong>{auth.currentUser?.email}</strong>.
          Click the link and this page will update automatically.
        </p>

        {/* Live polling indicator */}
        <div className="verify-watching">
          <span className="verify-pulse-dot" />
          Waiting for verification…
        </div>

        {/* Feedback from manual check or resend */}
        {resendMsg && (
          <p className="auth-helper-text" style={{ marginTop: '12px', textAlign: 'center' }}>
            {resendMsg}
          </p>
        )}

        {/* Manual fallback — in case polling misses it */}
        <button
          className="btn-outline btn-full"
          onClick={handleCheck}
          disabled={checking}
          style={{ marginTop: '20px' }}
        >
          {checking ? 'Checking…' : "I've verified my email"}
        </button>

        {/* Resend with 60-second cooldown */}
        <p className="auth-helper-text" style={{ marginTop: '20px', textAlign: 'center' }}>
          Didn't get the email?{' '}
          {resendCooldown > 0
            ? <span style={{ color: '#999' }}>Resend in {resendCooldown}s</span>
            : (
              <button className="link-btn" onClick={handleResend}>
                Resend verification email
              </button>
            )
          }
        </p>

        <p className="auth-helper-text" style={{ marginTop: '8px', textAlign: 'center' }}>
          <a href="/login" style={{ color: '#4a7c59' }}>Back to login</a>
        </p>

      </div>
    </div>
  );
}

export default VerifyEmail;
